'use client';

import React, { useState } from 'react';
import { motion } from 'framer-motion';

export default function SubscribeForm() {
  const [email, setEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [emailError, setEmailError] = useState('');
  const [formStatus, setFormStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');

  const validateEmail = (email: string) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newEmail = e.target.value;
    setEmail(newEmail);
    if (!validateEmail(newEmail) && newEmail.length > 0) {
      setEmailError('Будь ласка, введіть коректну електронну адресу');
    } else {
      setEmailError('');
    }
  };

  const handleSubscribe = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateEmail(email)) {
      setEmailError('Будь ласка, введіть коректну електронну адресу');
      return;
    }

    setFormStatus('loading');
    setIsLoading(true);

    try {
      await new Promise((resolve) => setTimeout(resolve, 1500));
      setFormStatus('success');
      setEmail('');
    } catch {
      setFormStatus('error');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, delay: 0.2 }}
      className="bg-[#1A3365] text-white rounded-xl p-8 shadow-lg"
    >
      <div className="max-w-2xl mx-auto text-center">
        <h2 className="text-2xl font-bold mb-4">Підпишіться на оновлення</h2>
        <p className="mb-6">Отримуйте найсвіжіші статті та новини прямо на вашу електронну пошту</p>
        {formStatus === 'success' ? (
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-green-600 text-white p-4 rounded-lg"
          >
            Дякуємо за підписку! Ми надіслали вам лист для підтвердження.
          </motion.div>
        ) : (
          <form onSubmit={handleSubscribe} className="flex flex-col sm:flex-row gap-4">
            <div className="flex-grow">
              <input
                type="email"
                placeholder="Ваша електронна пошта"
                className={`w-full px-4 py-3 rounded-lg text-gray-800 focus:outline-none focus:ring-2 ${
                  emailError ? 'focus:ring-red-500' : 'focus:ring-[#ff7b00]'
                }`}
                value={email}
                onChange={handleEmailChange}
                required
                aria-label="Електронна пошта для підписки"
                aria-invalid={!!emailError}
                aria-describedby={emailError ? 'email-error' : undefined}
              />
              {emailError && (
                <p id="email-error" className="text-red-500 text-sm mt-1">
                  {emailError}
                </p>
              )}
            </div>
            <button
              type="submit"
              disabled={isLoading || !!emailError}
              className={`bg-[#ff7b00] hover:bg-[#ff7b00]/90 px-6 py-3 rounded-lg font-medium transition-all flex items-center justify-center min-w-[140px] ${
                isLoading || emailError ? 'opacity-50 cursor-not-allowed' : ''
              }`}
              aria-label="Підписатися на оновлення"
            >
              {isLoading ? (
                <>
                  <svg
                    className="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    ></circle>
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    ></path>
                  </svg>
                  Відправка...
                </>
              ) : (
                'Підписатися'
              )}
            </button>
          </form>
        )}
      </div>
    </motion.div>
  );
}